import classnames from 'classnames'
import * as React from 'react'

import { Variants } from '@/components/Button/Variants.tsx'
import { SEO_SPACER } from '@/lib/seoSpacer.ts'

import type { UTMParameters } from '@/lib/hooks/useUrlWithUtmParameters.ts'
import NordicLink from '../Button/NordicLink.tsx'
import styles from './Hero.module.scss'

export type HeroCta = {
	label: string
	href: string
	utmParams?: UTMParameters
}

export type HeroEyebrowVariant = 'blue' | 'orange' | 'green' | 'red'

export type HeroProps = {
	eyebrow?: string
	eyebrowVariant?: HeroEyebrowVariant
	title: React.ReactNode
	body?: React.ReactNode
	primaryCta?: HeroCta
	secondaryCta?: HeroCta
	media?: React.ReactNode
	className?: string
}

export const Hero: React.FC<HeroProps> = ({
	eyebrow,
	eyebrowVariant = 'blue',
	title,
	body,
	primaryCta,
	secondaryCta,
	media,
	className,
}) => (
	<section className={classnames(styles.heroWrapper, className)}>
		<div className={styles.hero}>
			<div className={styles.content}>
				{eyebrow && (
					<span
						className={classnames(styles.eyebrow, {
							// Note: blue is default
							[styles.eyebrowOrange]: eyebrowVariant === 'orange',
							[styles.eyebrowGreen]: eyebrowVariant === 'green',
							[styles.eyebrowRed]: eyebrowVariant === 'red',
						})}
					>
						<span className={styles.eyebrowDot} aria-hidden="true" />
						{eyebrow}
						{SEO_SPACER}
					</span>
				)}
				<div className={styles.copy}>
					<h1 className={styles.title}>
						{title}
						{SEO_SPACER}
					</h1>
					{body && (
						<p className={styles.body}>
							{body}
							{SEO_SPACER}
						</p>
					)}
					{(primaryCta || secondaryCta) && (
						<div className={styles.actions}>
							{primaryCta && (
								<NordicLink
									showSlant
									href={primaryCta.href}
									size="large"
									className={styles.primaryCta}
									preserveUtmParams
									utmParams={primaryCta.utmParams}
									data-testid="hero-primary-cta"
								>
									{primaryCta.label}
									{SEO_SPACER}
								</NordicLink>
							)}
							{secondaryCta && (
								<NordicLink
									variant={Variants.secondaryDefault}
									href={secondaryCta.href}
									size="large"
									className={styles.secondaryCta}
									preserveUtmParams
									utmParams={secondaryCta.utmParams}
									data-testid="hero-secondary-cta"
								>
									{secondaryCta.label}
									{SEO_SPACER}
								</NordicLink>
							)}
						</div>
					)}
				</div>
			</div>
			{media && <div className={styles.media}>{media}</div>}
		</div>
	</section>
)
